/**
 * Utility functions for any storefront wizard region
 * TO-DO: refactor StorefrontWizardRegion and possibly CentralizedWizardRegion to make use of this utils file
 */

import * as React from "react";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "src/components/flexFramework/domain/BlossomComponentFactory";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */
export const getWizardLOBFlexComponents = (flexModules: FlexComponent[]): FlexComponent[] => {
  return flexModules.filter((module: FlexComponent) => {
    return module && module.id && module.id.includes("wizard");
  });
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components corresponding to a wizard LOB form
 */
export const getWizardLOBFormChildren = (children: React.ReactElement[]): React.ReactElement[] => {
  return children.filter((child) => {
    const { key } = child;

    return key && key.toString().includes("wizard") && !key.toString().includes("biased");
  });
};

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */
export const getBiasedWizardLOBFlexComponent = (flexModules: FlexComponent[]): FlexComponent[] => {
  return flexModules.filter((module: FlexComponent) => {
    return module?.id?.includes("biased");
  });
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components that do not correspond to a wizard LOB form
 * Does not categorize react components further
 */
export const getNonWizardFormChildren = (children: React.ReactElement[]): React.ReactElement[] => {
  return children
    .filter((child) => {
      const { key } = child;

      return key && !key.toString().includes("wizard");
    })
    .map((child) => {
      const { key } = child;

      const childComponent = BlossomComponentFactory.get(child.props as BlossomComponentFactoryProps);
      const childComponentModel = childComponent?.model;
      if (childComponentModel && childComponentModel.horizontalCenter === false) {
        return (
          <div key={`parent-${key}`} className="prevent-justify-center">
            {child}
          </div>
        );
      }

      return <div key={`parent-${key}`}>{child}</div>;
    });
};

export const getWizardImageOverride = (
  children: React.ReactElement[],
  context: ExtendedContextStore
): string | undefined => {
  const siteid = context?.site?.id;
  let backgroundImage;
  children.forEach((child) => {
    const { key } = child;
    if (key && key.toString().includes("background-image-override")) {
      const childComponent = BlossomComponentFactory.get(child.props as BlossomComponentFactoryProps);
      const childSiteid = childComponent?.model?.siteid;
      if (childSiteid === siteid) {
        backgroundImage = childComponent?.model?.backgroundUrl;
      }
    }
  });
  return backgroundImage;
};

/**
 * Based on a list of ReactElement, will filter and return
 * the ones that are part of Packages as standalone LOBs
 * i.e. Hotels, Flights and Cars
 */
export const getStandaloneLOBModulesInPackages = (children: React.ReactElement[]) => {
  const standaloneLobsInPackages = ["wizard-hotel-pwa-v2", "wizard-flight-pwa", "wizard-car-pwa"];

  return children.filter((lobForm: React.ReactElement) => {
    const { name } = lobForm.props.flexComponent;

    return standaloneLobsInPackages.includes(name);
  });
};

/**
 * This function overrides the configs for the biased wizard
 * Which takes flexcomponent and wizardState as input.
 * Used in StoreFrontWizardRegionMeso
 */
export const getMesoWizardModuleData = (mesoWizardModule: FlexComponent[], wizardState: ClientWizardStateStore) => {
  const biasingModel = mesoWizardModule[0].model;

  const globalConfig = wizardState.globalWizardState.config;
  globalConfig.halfWizard = biasingModel.halfWizard;
  globalConfig.selectedProduct = biasingModel.selectedProduct;
  globalConfig.defaultOrigin = biasingModel.biasingOriginAttributeValue;

  globalConfig.defaultMultipleDestination =
    biasingModel.biasingDestinationAttributeMap !== undefined
      ? biasingModel.biasingDestinationAttributeMap[""]
      : undefined;
  globalConfig.defaultDestination = biasingModel.biasingDestinationAttributeValue;
  globalConfig.defaultStopover = biasingModel.biasingStopoverAttributeValue;
  globalConfig.defaultMultipleStopover = biasingModel.biasingStopoverAttributeMap
    ? biasingModel.biasingStopoverAttributeMap[""]
    : undefined;

  globalConfig.defaultMultipleOrigin =
    biasingModel.biasingOriginAttributeMap !== undefined ? biasingModel.biasingOriginAttributeMap[""] : undefined;
  globalConfig.destinationDropdownTypeahead = biasingModel.destDropdownTypeahead;
  globalConfig.calendarFilterCarrierCode = biasingModel.calendarFilterCarrierCode;
  globalConfig.displayBiasedWizard = !biasingModel.empty;
  globalConfig.userLocation = biasingModel.airportName;
  globalConfig.destiantionType = biasingModel.destinationBiased;
  globalConfig.originType = biasingModel.originBiased;

  const hotelConfig = wizardState.hotelWizardState.config;
  wizardState.hotelWizardState.overrideConfig(() => {
    hotelConfig.hotelID = biasingModel.biasingAttributeHotelValue;
    hotelConfig.hotelName = biasingModel.biasingAttributeHotelName;
    hotelConfig.hotelChainID = biasingModel.biasingAttributeHotelChainID;
    hotelConfig.multiHotelIDs =
      biasingModel.biasingAttributeMultiHotelValue && biasingModel.biasingAttributeMultiHotelValue.split(";");
    hotelConfig.isBiasedWizardEmpty = biasingModel.empty;
  });

  const carConfig = wizardState.carWizardState.config;
  wizardState.carWizardState.overrideConfig(() => {
    carConfig.carType = biasingModel.biasingAttributeCarType;
    carConfig.carVendorCode = biasingModel.biasingAttributeCarVendorCode;
    carConfig.carVendorName = biasingModel.biasingAttributeCarVendorName;
    carConfig.carDiscountTypeCode = biasingModel.carDiscountTypeCode;
    carConfig.carDiscountTypeName = biasingModel.carDiscountTypeName;
    carConfig.carDiscountCode = biasingModel.carDiscountCode;
    carConfig.showCarDiscount = biasingModel.showCarDiscount;
    carConfig.isBiasedWizardEmpty = biasingModel.empty;
  });

  const cruiseConfig = wizardState.cruiseWizardState.config;
  wizardState.cruiseWizardState.overrideConfig(() => {
    cruiseConfig.cruiseLine = biasingModel.biasingAttributeCruiseLine;
  });

  const flightConfig = wizardState.flightWizardState.config;
  wizardState.flightWizardState.overrideConfig(() => {
    flightConfig.airlineOptions = biasingModel.airlineOptions;
    flightConfig.cabinClass = biasingModel.biasedCabinClass;
    if (!!globalConfig.defaultStopover || !!globalConfig.defaultMultipleStopover) {
      flightConfig.multiCityFlight.maxNumberOfAdditionalLegs = 1;
      flightConfig.multiCityFlight.minNumberOfLegs = 1;

      const getOriginValue = () => {
        if (globalConfig.defaultStopover) {
          return {
            stopoverName: globalConfig.defaultStopover,
            stopoverRegionId: "",
          };
        }
        if (globalConfig.defaultMultipleStopover && globalConfig.defaultMultipleStopover.length > 0) {
          const value = globalConfig.defaultMultipleStopover[0].split(":");

          return {
            stopoverName: value[0],
            stopoverRegionId: value[1],
          };
        }

        return {
          stopoverName: "",
          stopoverRegionId: "",
        };
      };

      const { stopoverName, stopoverRegionId } = getOriginValue();

      wizardState.flightWizardState.listOfAdditionalLegs.push({
        location: {
          origin: {
            value: stopoverName,
            metaData: {
              regionId: stopoverRegionId,
              ttla: "",
              latLong: "",
              countryCode: "",
            },
          },
          destination: {
            value: "",
            metaData: {
              regionId: "",
              ttla: "",
              latLong: "",
              countryCode: "",
            },
          },
        },
        date: wizardState.globalWizardState.getDateFromConfig(wizardState.flightWizardState.config.date),
      });
    }
  });

  const packageConfig = wizardState.packageWizardState.config;
  wizardState.packageWizardState.overrideConfig(() => {
    packageConfig.multiHotelIDs =
      biasingModel.biasingAttributeMultiHotelValue && biasingModel.biasingAttributeMultiHotelValue.split(";");
  });
};
